import IamService from "@/services/iamService"
import CustomerService from '@/services/customerService'
import { defineStore } from 'pinia'
import { AxiosError } from "axios"
import { IFinalizeRecoverMsisdnPayload, IFinalizeRecoverPayload, IFinalizeRecoverUserIdPayload } from "@/interface/services/request/user-payload.interface"

export const useRecoveryStore = defineStore('recovery', {
    state: (): IRecoveryState => ({
        guid: '',
        token: '',
        customer: '',
        invalid: false,
        enableSms: false,
        createAccount: false,
    }),
    actions: {
        async initRecovery(uuid: string, token: string, isCreate: boolean): Promise<boolean> {
            this.$reset()

            try {
                const response = await IamService.initRecovery(uuid, token, isCreate)
                if (response.user) {
                    if (response.user.customer_ref && response.user.customer_ref !== '') {
                        this.customer = response.user.customer_ref
                    } else {
                        this.customer = response.user.email
                    }
                } else {
                    this.customer = ''
                }
                
                this.invalid = false
                this.guid = uuid
                this.token = token
                this.createAccount = isCreate
            }
            catch (error) {
                if (error instanceof AxiosError && error.response) {
                    this.invalid = (error.response.status == 404 || error.response.status == 419)
                } else {
                    throw error
                }
            }

            return true
        },
        async initSmsRecovery(isCreate = false): Promise<boolean> {
            this.$reset()
            this.enableSms = true
            this.createAccount = isCreate
            return true
        },
        async saveRecovery(customer: string, password: string, isCreate: boolean): Promise<number> {
            if (!this.guid || !this.token) {
                return -1
            }

            const payload = <IFinalizeRecoverPayload>{}
            payload.customer = customer
            payload.pwd = password

            try {
                await IamService.saveRecovery(this.guid, this.token, isCreate, payload)
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        async saveCreateAccount(msisdn: string, code: string, password: string): Promise<number> {
            if (!this.enableSms) {
                return -1
            }

            const payload = <IFinalizeRecoverMsisdnPayload>{}
            payload.pwd = password

            try {
                await IamService.saveCreateAccount(msisdn, code, payload)
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        async saveRecoveryMsisdn(msisdn: string, code: string, password: string): Promise<number> {
            if (!this.enableSms) {
                return -1
            }

            const payload = <IFinalizeRecoverMsisdnPayload>{}
            payload.pwd = password

            try {
                await IamService.saveRecoveryMsisdn(msisdn, code, payload)
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        async saveRecoveryUserId(oldPwd: string, newPwd: string): Promise<number> {
            const payload = <IFinalizeRecoverUserIdPayload>{}
            payload.old_pwd = oldPwd
            payload.pwd = newPwd

            try {
                await CustomerService.saveRecoveryLoggedUser(payload) 
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        reset(): void {
            this.$reset()
        }
    },
    getters: {
        getCustomer(state: IRecoveryState): string {
            return state.customer ? state.customer : ''
        },
        isAuthenticated(state: IRecoveryState): boolean {
            return state.token !== undefined && state.token !== '';
        },
        isSms(state: IRecoveryState): boolean {
            return state.enableSms ?? false
        },
        isCreateAccountProcess(state: IRecoveryState): boolean {
            return state.createAccount ?? false
        }
    },
    persist: {
        storage: sessionStorage
    },
})

interface IRecoveryState {
    guid: string,
    token: string
    customer?: string,
    invalid?: boolean,
    enableSms?: boolean,
    createAccount?: boolean
}

export type RecoveryStore = ReturnType<typeof useRecoveryStore>